import { jsx, jsxs } from "react/jsx-runtime";
import { RotateCw } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { getHeadersWithUniqueVisitorId } from "../../../Auth/index.js";
import moment from "moment";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "../../../Components/ui/drawer.js";
import { useConciergeContext } from "../../ConciergeContextProvider.js";
const formatTimestamp = (timestamp) => {
  const m = moment(timestamp);
  const now = moment();
  const diffInMinutes = now.diff(m, "minutes");
  const diffInHours = now.diff(m, "hours");
  const diffInDays = now.diff(m, "days");
  if (diffInMinutes < 1) {
    return "Just now";
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  } else if (diffInDays === 1) {
    return "Yesterday";
  } else if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  } else {
    const formatString = m.year() === now.year() ? "MMM D" : "MMM D, YYYY";
    return m.format(formatString);
  }
};
const fetchConversations = async (apiBaseUrl) => {
  const headers = await getHeadersWithUniqueVisitorId();
  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/conversations`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...headers
      // Includes Authorization, x-bc-visitor-id, X-Client-AppId
    }
  });
  if (!response.ok) {
    throw new Error("Failed to fetch conversations");
  }
  return response.json();
};
const ConversationHistoryDrawer = ({ open, onClose, onConversationSelect }) => {
  const { apiBaseUrl } = useConciergeContext();
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["conversations", apiBaseUrl],
    queryFn: () => fetchConversations(apiBaseUrl),
    enabled: open,
    // Only fetch when drawer is open
    staleTime: 5 * 60 * 1e3,
    // 5 minutes
    gcTime: 10 * 60 * 1e3
    // 10 minutes (renamed from cacheTime in v5)
  });
  const handleConversationClick = (conversationId, mode) => {
    onConversationSelect == null ? void 0 : onConversationSelect(conversationId, mode);
    onClose();
  };
  return /* @__PURE__ */ jsx(
    Drawer,
    {
      open,
      onOpenChange: (isOpen) => !isOpen && onClose(),
      direction: "left",
      children: /* @__PURE__ */ jsx(DrawerContent, { className: "tw:h-full tw:w-80 tw:rounded-none", children: /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:h-full tw:flex-col tw:bg-white", children: [
        /* @__PURE__ */ jsxs(DrawerHeader, { className: "tw:flex tw:flex-row tw:items-center tw:justify-between tw:px-3 tw:py-3 tw:space-y-0", children: [
          /* @__PURE__ */ jsx(DrawerTitle, { className: "tw:text-sm tw:font-semibold", children: "Chats" }),
          /* @__PURE__ */ jsx(
            "button",
            {
              onClick: () => refetch(),
              disabled: isFetching,
              "aria-label": "Refresh conversations",
              className: "tw:inline-flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-lg tw:text-gray-600 hover:tw:bg-gray-100 hover:tw:text-gray-900 tw:transition-colors disabled:tw:opacity-50 tw:cursor-pointer",
              children: /* @__PURE__ */ jsx(
                RotateCw,
                {
                  size: 16,
                  strokeWidth: 2,
                  className: isFetching ? "tw:animate-spin" : ""
                }
              )
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "tw:flex-1 tw:overflow-y-auto", children: [
          isLoading && /* @__PURE__ */ jsx("div", { className: "tw:flex tw:items-center tw:justify-center tw:p-4", children: /* @__PURE__ */ jsx("div", { className: "tw:h-5 tw:w-5 tw:animate-spin tw:rounded-full tw:border-2 tw:border-gray-300 tw:border-t-gray-900" }) }),
          error && /* @__PURE__ */ jsx("div", { className: "tw:mx-3 tw:my-2 tw:rounded-lg tw:bg-red-50 tw:p-3 tw:text-sm tw:text-red-800", children: "Failed to load conversations. Please try again." }),
          data && data.conversations.length === 0 && /* @__PURE__ */ jsx("div", { className: "tw:p-6 tw:text-center tw:text-sm tw:text-gray-500", children: "No conversations yet. Start a new conversation to see your history here." }),
          data && data.conversations.length > 0 && /* @__PURE__ */ jsx("div", { children: data.conversations.map((conversation) => /* @__PURE__ */ jsxs(
            "button",
            {
              onClick: () => handleConversationClick(conversation.conversationId, conversation.mode),
              className: "tw:w-full tw:px-3 tw:py-2.5 tw:text-left tw:transition-colors tw:group tw:cursor-pointer",
              style: {
                backgroundColor: "transparent"
              },
              onMouseEnter: (e) => {
                e.currentTarget.style.backgroundColor = "#f9fafb";
              },
              onMouseLeave: (e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              },
              children: [
                /* @__PURE__ */ jsx("div", { className: "tw:line-clamp-2 tw:text-sm tw:font-medium tw:text-gray-900", children: conversation.title || conversation.preview }),
                /* @__PURE__ */ jsx("div", { className: "tw:mt-1 tw:text-xs tw:text-gray-500", children: formatTimestamp(conversation.lastUpdated) })
              ]
            },
            conversation.conversationId
          )) })
        ] })
      ] }) })
    }
  );
};
export {
  ConversationHistoryDrawer
};
//# sourceMappingURL=index.js.map
